import React, { useEffect, useState } from "react"
import * as url from "../../helpers/url_helper"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    FormFeedback,
    FormGroup,
    Input,
    Label,
    Row,
    Modal,
    ModalHeader,
    ModalBody,
} from "reactstrap"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"

import {
    getMerchantGroupContacts,
    manageMerchantGroupContact,
    getMerchantGroupContactCommunications,
    manageMerchantGroupContactCommunication,
    setMerchantGroupContactCreationState,
    clearMerchantGroupContactCreationState,
} from "store/merchant-group/actions"
import {
    getCommChannels,
} from "store/actions"
import SelectInput from "components/Common/SelectInput"
import Dropzone from "react-dropzone"
import {
    convertFileToBase64,
    formatBytes,
    validateImageFile,
} from "helpers/file_helper"
import { validateBeforeConfirm } from "helpers/validation_helper"
import ConfirmButton from "components/Common/ConfirmButton"
import TextInput from "components/Common/TextInput"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import SimpleTableContainer from "components/Common/SimpleTableContainer"
import AccessControlWrapper from "components/Common/PermissionButton"

export default function MerchantGroupContactForm({
    page,
    match,
    history,
    activeTab,
    tabId,
    createdMerchantGroupID,
}) {
    const [readOnly, setReadOnly] = useState(page === "detail")
    const [showAddForm, setShowAddForm] = useState(page === "add")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selectedContact, setSelectedContact] = useState(null)
    const [showCommunicationsModal, setShowCommunicationsModal] = useState(false)
    const [showAddCommunication, setShowAddCommunication] = useState(false)
    const [editingCommunication, setEditingCommunication] = useState(null)
    const [editingCommId, setEditingCommId] = useState(null)
    const [editCommValue, setEditCommValue] = useState("")
    const [selectedFiles, setSelectedFiles] = useState([])
    const [communications, setCommunications] = useState([
        { commChannelID: "", channelValue: "" },
    ])
    const [editingContact, setEditingContact] = useState(false)
    const [contactSelectedFiles, setContactSelectedFiles] = useState([])
    const [isUpdatingContact, setIsUpdatingContact] = useState(false)
    const [previousContactsLength, setPreviousContactsLength] = useState(0)

    // State for two-phase contact creation - simplified to single communication
    const [currentCommunication, setCurrentCommunication] = useState({
        commChannelID: "",
        channelValue: "",
    })
    const [isSubmittingCommunication, setIsSubmittingCommunication] =
        useState(false)

    const dispatch = useDispatch()
    const detailID = match?.params?.id

    // Use createdMerchantGroupID in add mode, detailID in detail mode
    const merchantGroupID = page === "add" ? createdMerchantGroupID : detailID

    // Helper function to check if error should be displayed
    const shouldDisplayError = error => {
        if (!error) return false

        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""

        // Check for common "no data found" patterns
        const noDataPatterns = ["no content available"]

        return !noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }

    const {
        data,
        contactError,
        isLoadingContacts,
        contactCommunications,
        contactCommunicationError,
        isLoadingContactCommunications,
        commChannels,
        contactCreationState,
    } = useSelector(state => ({
        data: state.merchantGroup,
        contactError: state.merchantGroup.contactError,
        isLoadingContacts: state.merchantGroup.isLoadingContacts || false,
        contactCommunications:
            state.merchantGroup.merchantGroupContactCommunications || [],
        contactCommunicationError: state.merchantGroup.contactCommunicationError,
        isLoadingContactCommunications:
            state.merchantGroup.isLoadingContactCommunications || false,
        commChannels: state.appInfo.commChannels || [],
        contactCreationState: state.merchantGroup.contactCreationState,
    }))

    const contacts = data?.merchantGroupContacts || []

    // Fetch contacts only when tab is active
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (detailID && isTabActive) {
            dispatch(
                getMerchantGroupContacts({
                    headers: {
                        "input-parameters": { merchantGroupID: detailID },
                    },
                })
            )
        }
    }, [dispatch, detailID, activeTab, tabId])

    // Fetch communication channels if not loaded
    useEffect(() => {
        if (!commChannels.length) {
            dispatch(
                getCommChannels({
                    headers: {
                        "input-parameters": {},
                    },
                })
            )
        }
    }, [dispatch, commChannels.length])

    // Initialize contact creation state on component mount
    useEffect(() => {
        // Always start with fresh contact creation state when component mounts
        dispatch(clearMerchantGroupContactCreationState())
        // Also clear any communication data
        dispatch({
            type: "GET_MERCHANT_GROUP_CONTACT_COMMUNICATIONS_SUCCESS",
            payload: [],
        })
    }, [dispatch]) // Only run on mount to reset state

    // Update selectedContact when contacts list changes
    useEffect(() => {
        if (selectedContact && contacts.length > 0) {
            const updatedContact = contacts.find(
                contact =>
                    contact.merchantGroupContactID ===
                    selectedContact.merchantGroupContactID
            )
            if (updatedContact) {
                setSelectedContact(updatedContact)
            }
        }
    }, [contacts, selectedContact?.merchantGroupContactID])

    // Track contacts length changes
    useEffect(() => {
        setPreviousContactsLength(contacts.length)
    }, [contacts.length])

    // Handle accepted files for photo upload
    async function handleAcceptedFiles(files) {
        if (files.length > 0) {
            const file = files[0]
            const base64Img = await convertFileToBase64(file)

            try {
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                    formattedSize: formatBytes(file.size),
                    forExistingResource: false,
                    base64encodedFileByteArray: base64Img,
                    resourceType: "EUROPA_MERCHANT_GROUP_CONTACT_PHOTO",
                })
                setSelectedFiles([file])
                validation.setFieldValue("photo", file)
            } catch (error) {
                console.error("Error converting to base64:", error)
            }
        }
    }

    // Function to handle contact submission after confirmation
    const handleContactSubmit = async values => {
        setIsSubmitting(true)

        const payload = {
            merchantGroupID: merchantGroupID,
            contactName: values.contactName,
            contactDesignation: values.contactDesignation,
            photo: values.photo || "",
            actionType: "create",
            // Remove communications completely from contact creation
        }

        dispatch(manageMerchantGroupContact(payload, {}, history))
    }

    // Form validation for contact only
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            contactName: contactCreationState.currentContact?.contactName || "",
            contactDesignation:
                contactCreationState.currentContact?.contactDesignation || "",
            photo: contactCreationState.currentContact?.photo || "",
        },
        validationSchema: Yup.object({
            contactName: Yup.string()
                .required("Please enter contact name")
                .max(255, "Contact name cannot be greater than 255 characters"),
            contactDesignation: Yup.string()
                .required("Please enter contact designation")
                .max(255, "Contact designation cannot be greater than 255 characters"),
            photo: Yup.mixed()
                .nullable()
                .test("contactPhoto", "Invalid contact photo", async function (value) {
                    if (!value) return true

                    try {
                        await validateImageFile(value, "contact", 550, 550, 1)
                        return true
                    } catch (error) {
                        return this.createError({ message: error.message })
                    }
                }),
        }),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleContactSubmit(values)
            }
        },
    })

    // Form validation for communication phase - simplified to single communication
    const communicationPhaseValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            commChannelID: currentCommunication.commChannelID,
            channelValue: currentCommunication.channelValue,
        },
        validationSchema: Yup.object({
            commChannelID: Yup.string().test(
                "required-when-value",
                "Communication channel is required",
                function (value) {
                    const { channelValue } = this.parent

                    if (
                        channelValue &&
                        channelValue.trim() !== "" &&
                        (!value || value.trim() === "")
                    ) {
                        return this.createError({
                            message: "Communication channel is required",
                        })
                    }
                    return true
                }
            ),
            channelValue: Yup.string().test(
                "required-and-format",
                "Channel value is required",
                function (value) {
                    const { commChannelID } = this.parent

                    if (
                        commChannelID &&
                        typeof commChannelID === "string" &&
                        commChannelID.trim() !== "" &&
                        (!value || value.trim() === "")
                    ) {
                        return this.createError({
                            message: "Channel value is required",
                        })
                    }

                    if (!commChannelID || !value) return true

                    const channel = commChannels.find(
                        ch => ch.commChannelID === commChannelID
                    )

                    if (!channel) return true

                    const channelName = channel.meaning || channel.name || ""

                    if (channelName.toLowerCase().includes("email")) {
                        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                            return this.createError({
                                message: "Please enter a valid email address",
                            })
                        }
                    }

                    if (
                        channelName.toLowerCase().includes("phone") ||
                        channelName.toLowerCase().includes("mobile") ||
                        channelName.toLowerCase().includes("whatsapp")
                    ) {
                        if (
                            !/^[+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/.test(
                                value
                            )
                        ) {
                            return this.createError({
                                message: "Please enter a valid phone number",
                            })
                        }
                    }

                    return true
                }
            ),
        }),
        onSubmit: async values => {

            setIsSubmittingCommunication(true)

            // Check if communication is valid
            if (
                !values.commChannelID ||
                !values.channelValue ||
                values.channelValue.trim() === ""
            ) {
                setIsSubmittingCommunication(false)
                return
            }

            // Submit the single communication
            const payload = {
                mgContactID: contactCreationState.currentContact.merchantGroupContactID,
                commChannelID: values.commChannelID,
                channelValue: values.channelValue,
                actionType: "create",
            }
            dispatch(manageMerchantGroupContactCommunication(payload, {}, history))

            // Clear the form after successful submission
            setCurrentCommunication({ commChannelID: "", channelValue: "" })
            communicationPhaseValidation.resetForm()
            // Also explicitly set the field values to ensure Select component updates
            communicationPhaseValidation.setFieldValue("commChannelID", "")
            communicationPhaseValidation.setFieldValue("channelValue", "")

            setIsSubmittingCommunication(false)
        },
    })

    // Monitor for successful contact submission and transition to communication phase
    useEffect(() => {
        // Check if a new contact was added (length increased)
        if (
            contacts.length > previousContactsLength &&
            !contactError &&
            contactCreationState.phase === "contact"
        ) {
            // Get the newly created contact
            const newContact = contacts[contacts.length - 1]

            // Transition to communication phase
            dispatch(
                setMerchantGroupContactCreationState({
                    phase: "communication",
                    currentContact: { ...newContact, isExistingContact: false },
                    isContactReadOnly: true,
                })
            )

            setSelectedFiles([])
            setIsSubmitting(false)
            setPreviousContactsLength(contacts.length)
        } else if (contactError && isSubmitting) {
            // Handle contact creation error
            setIsSubmitting(false)
        }
    }, [
        contactError,
        contacts.length,
        previousContactsLength,
        isSubmitting,
        contactCreationState.phase,
        dispatch,
    ])

    // Monitor communication submission results
    useEffect(() => {
        if (
            contactCreationState.phase === "communication" &&
            isSubmittingCommunication &&
            contactCommunicationError
        ) {
            // Handle communication errors (optimistic approach - don't rollback contact)
            // Stop submitting but keep the form open for retry
            setIsSubmittingCommunication(false)
            // Communication error doesn't affect the contact, just show error and allow retry
            // DON'T call handleFinishCommunications() or handleSkipCommunications() here
        }
    }, [
        contactCommunicationError,
        contactCreationState.phase,
        isSubmittingCommunication,
    ])

    // Load communications for the current contact when in communication phase
    // Only load for existing contacts, not newly created ones
    useEffect(() => {
        if (
            contactCreationState.phase === "communication" &&
            contactCreationState.currentContact?.merchantGroupContactID &&
            showAddForm &&
            contactCreationState.currentContact?.isExistingContact // Only load for existing contacts
        ) {
            // Clear any existing communication data first to prevent showing wrong data
            dispatch({
                type: "GET_MERCHANT_GROUP_CONTACT_COMMUNICATIONS_SUCCESS",
                payload: [],
            })

            // Then load the correct communications
            dispatch(
                getMerchantGroupContactCommunications({
                    headers: {
                        "input-parameters": {
                            mgContactID:
                                contactCreationState.currentContact.merchantGroupContactID,
                        },
                    },
                })
            )
        } else if (
            contactCreationState.phase === "communication" &&
            contactCreationState.currentContact?.merchantGroupContactID &&
            showAddForm &&
            !contactCreationState.currentContact?.isExistingContact
        ) {
            // For newly created contacts, just clear the communications array
            dispatch({
                type: "GET_MERCHANT_GROUP_CONTACT_COMMUNICATIONS_SUCCESS",
                payload: [],
            })
        }
    }, [
        contactCreationState.phase,
        contactCreationState.currentContact?.merchantGroupContactID,
        contactCreationState.currentContact?.isExistingContact,
        showAddForm,
        dispatch,
    ])

    // Helper functions for phase management
    const handleSkipCommunications = () => {
        // Reset to initial state to allow adding another contact
        dispatch(clearMerchantGroupContactCreationState())
        // Clear communication data
        dispatch({
            type: "GET_MERCHANT_GROUP_CONTACT_COMMUNICATIONS_SUCCESS",
            payload: [],
        })

        setIsSubmittingCommunication(false)
        validation.resetForm()
        if (page !== "add") {
            setShowAddForm(false)
        }
    }

    const handleFinishCommunications = () => {
        // Similar to skip but indicates completion
        setIsSubmittingCommunication(false)
        setTimeout(() => {
            dispatch(clearMerchantGroupContactCreationState())
            // Clear communication data
            dispatch({
                type: "GET_MERCHANT_GROUP_CONTACT_COMMUNICATIONS_SUCCESS",
                payload: [],
            })

            if (page !== "add") {
                setShowAddForm(false)
            }
        }, 1000)
    }

    const handleCancelContactCreation = () => {
        // Cancel the entire contact creation process
        dispatch(clearMerchantGroupContactCreationState())
        // Clear communication data
        dispatch({
            type: "GET_MERCHANT_GROUP_CONTACT_COMMUNICATIONS_SUCCESS",
            payload: [],
        })

        validation.resetForm()
        setSelectedFiles([])
        if (page !== "add") {
            setShowAddForm(false)
        }
    }

    // Communication management functions - simplified for single communication
    const updateCommunicationField = (field, value) => {
        setCurrentCommunication(prev => ({ ...prev, [field]: value }))
        communicationPhaseValidation.setFieldValue(field, value)
    }

    // Function to handle communication modal submission after confirmation
    const handleCommunicationModalSubmit = values => {
        const payload = {
            mgContactID: selectedContact.merchantGroupContactID,
            commChannelID: values.commChannelID,
            channelValue: values.channelValue,
            actionType: editingCommunication ? "update" : "create",
        }

        if (editingCommunication) {
            payload.mgContactCommunicationID =
                editingCommunication.mgContactCommunicationID
        }

        dispatch(manageMerchantGroupContactCommunication(payload, {}, history))
        handleCancelCommunication()
    }

    // Function to handle contact edit submission after confirmation
    const handleContactEditSubmit = async values => {
        setIsUpdatingContact(true)

        let finalPhotoValue = values.photo || ""

        // If it's a new file upload, keep the file object for processing
        // If it's an existing photo or empty, use the string value
        if (values.photo && values.photo.base64encodedFileByteArray) {
            finalPhotoValue = values.photo
        } else if (typeof values.photo === "string") {
            finalPhotoValue = values.photo
        } else if (!values.photo) {
            finalPhotoValue = ""
        }

        const payload = {
            merchantGroupID: merchantGroupID,
            merchantGroupContactID: selectedContact.merchantGroupContactID,
            contactName: values.contactName,
            contactDesignation: values.contactDesignation,
            photo: finalPhotoValue,
            actionType: "update",
        }

        dispatch(manageMerchantGroupContact(payload, {}, history))

        setTimeout(() => {
            setIsUpdatingContact(false)
            setEditingContact(false)
            setContactSelectedFiles([])
        }, 500)
    }

    // Form validation for communication
    const communicationValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            commChannelID: editingCommunication?.commChannel?.commChannelID || "",
            channelValue: editingCommunication?.channelValue || "",
        },
        validationSchema: Yup.object({
            commChannelID: Yup.string().required(
                "Please select a communication channel"
            ),
            channelValue: Yup.string()
                .required("Please enter the channel value")
                .test("channel-format", "Invalid format", function (value) {
                    const { commChannelID } = this.parent
                    const channel = commChannels.find(
                        ch => ch.commChannelID === commChannelID
                    )

                    if (!channel || !value) return true

                    const channelName = channel.meaning || channel.name || ""

                    if (channelName.toLowerCase().includes("email")) {
                        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                    }

                    if (
                        channelName.toLowerCase().includes("phone") ||
                        channelName.toLowerCase().includes("mobile") ||
                        channelName.toLowerCase().includes("whatsapp")
                    ) {
                        return /^[+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/.test(
                            value
                        )
                    }

                    return true
                }),
        }),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(communicationValidation)
            if (isValid) {
                handleCommunicationModalSubmit(values)
            }
        },
    })

    // Form validation for contact editing in modal
    const contactEditValidation = useFormik({
        enableReinitialize: true,
        initialValues: {
            contactName: selectedContact?.contactName || "",
            contactDesignation: selectedContact?.contactDesignation || "",
            photo: selectedContact?.photo || "",
        },
        validationSchema: Yup.object({
            contactName: Yup.string()
                .required("Please enter contact name")
                .max(255, "Contact name cannot be greater than 255 characters"),
            contactDesignation: Yup.string()
                .required("Please enter contact designation")
                .max(255, "Contact designation cannot be greater than 255 characters"),
            photo: Yup.mixed()
                .nullable()
                .test("contactPhoto", "Invalid contact photo", async function (value) {
                    if (!value) return true

                    try {
                        await validateImageFile(value, "contact", 550, 550, 1)
                        return true
                    } catch (error) {
                        return this.createError({ message: error.message })
                    }
                }),
        }),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(contactEditValidation)
            if (isValid) {
                handleContactEditSubmit(values)
            }
        },
    })

    // Handle contact photo upload in modal
    async function handleContactPhotoFiles(files) {
        if (files.length > 0) {
            const file = files[0]
            const base64Img = await convertFileToBase64(file)

            try {
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                    formattedSize: formatBytes(file.size),
                    forExistingResource: false,
                    base64encodedFileByteArray: base64Img,
                    resourceType: "EUROPA_MERCHANT_GROUP_CONTACT_PHOTO",
                })
                setContactSelectedFiles([file])
                contactEditValidation.setFieldValue("photo", file)
            } catch (error) {
                console.error("Error converting to base64:", error)
            }
        }
    }

    const clearContactPhoto = () => {
        setContactSelectedFiles([])
        contactEditValidation.setFieldValue("photo", "")
    }

    const handleEditContact = () => {
        setEditingContact(true)
        setContactSelectedFiles([])
        // Initialize photo if exists
        if (selectedContact?.photo) {
            const photoFile = {
                preview: selectedContact.photo,
            }
            setContactSelectedFiles([photoFile])
        }
    }

    const handleCancelContactEdit = () => {
        setEditingContact(false)
        setContactSelectedFiles([])
        contactEditValidation.resetForm()
    }

    const handleAddNewContact = () => {
        // Reset all form states
        validation.resetForm()
        setSelectedFiles([])
        setCommunications([{ commChannelID: "", channelValue: "" }])
        setCurrentCommunication({ commChannelID: "", channelValue: "" })

        // Reset contact creation state to start fresh
        dispatch(clearMerchantGroupContactCreationState())

        // Show the form
        setShowAddForm(true)
    }

    const addCommunicationField = () => {
        const newCommunications = [
            ...validation.values.communications,
            { commChannelID: "", channelValue: "" },
        ]
        validation.setFieldValue("communications", newCommunications)
    }

    const removeCommunicationField = index => {
        const newCommunications = validation.values.communications.filter(
            (_, i) => i !== index
        )
        validation.setFieldValue("communications", newCommunications)
    }

    const handleDeleteContact = contact => {
        const payload = {
            merchantGroupID: merchantGroupID,
            merchantGroupContactID: contact.merchantGroupContactID,
            actionType: "delete",
        }
        dispatch(manageMerchantGroupContact(payload, {}, history))
    }

    const handleCancelContact = () => {
        // Hide form in detail mode when cancel is clicked
        if (page === "detail") {
            setShowAddForm(false)
        }
        validation.resetForm()
        setSelectedFiles([])
        setCommunications([{ commChannelID: "", channelValue: "" }])
        setCurrentCommunication({ commChannelID: "", channelValue: "" })
    }

    const handleViewCommunications = contact => {
        setSelectedContact(contact)
        setShowCommunicationsModal(true)
        setEditingContact(false)
        setContactSelectedFiles([])
        setShowAddCommunication(false)
        setEditingCommId(null)
        setEditCommValue("")

        // Fetch communications for this contact
        dispatch(
            getMerchantGroupContactCommunications({
                headers: {
                    "input-parameters": { mgContactID: contact.merchantGroupContactID },
                },
            })
        )
    }

    const handleAddCommunication = () => {
        setEditingCommunication(null)
        communicationValidation.resetForm()
        setShowAddCommunication(true)
    }

    const handleEditCommunication = communication => {
        setEditingCommId(communication.mgContactCommunicationID)
        setEditCommValue(communication.channelValue)
    }

    const handleSaveCommunicationEdit = communication => {
        const channel = communication.commChannel
        const channelName = channel.meaning || channel.name || ""

        // Validate based on channel type
        let isValid = true
        let errorMsg = ""

        if (channelName.toLowerCase().includes("email")) {
            isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editCommValue)
            errorMsg = "Please enter a valid email address"
        } else if (
            channelName.toLowerCase().includes("phone") ||
            channelName.toLowerCase().includes("mobile") ||
            channelName.toLowerCase().includes("whatsapp")
        ) {
            isValid =
                /^[+]?[(]?[0-9]{1,3}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,9}$/.test(
                    editCommValue
                )
            errorMsg = "Please enter a valid phone number"
        }

        if (!editCommValue.trim()) {
            return
        }

        if (!isValid) {
            return
        }

        const payload = {
            mgContactID: selectedContact.merchantGroupContactID,
            mgContactCommunicationID: communication.mgContactCommunicationID,
            commChannelID: communication.commChannel.commChannelID,
            channelValue: editCommValue,
            actionType: "update",
        }

        dispatch(manageMerchantGroupContactCommunication(payload, {}, history))
        setEditingCommId(null)
        setEditCommValue("")
    }

    const handleCancelCommunicationEdit = () => {
        setEditingCommId(null)
        setEditCommValue("")
    }

    const handleDeleteCommunication = communication => {
        const payload = {
            mgContactID: selectedContact.merchantGroupContactID,
            mgContactCommunicationID: communication.mgContactCommunicationID,
            commChannelID: communication.commChannel.commChannelID,
            actionType: "delete",
        }
        dispatch(manageMerchantGroupContactCommunication(payload, {}, history))
    }

    const handleCancelCommunication = () => {
        setShowAddCommunication(false)
        setEditingCommunication(null)
        communicationValidation.resetForm()
    }

    const clearPhoto = () => {
        setSelectedFiles([])
        validation.setFieldValue("photo", "")
    }

    const getChannelIcon = channel => {
        if (channel.image) {
            return (
                <img
                    src={channel.image}
                    alt={channel.name}
                    style={{ width: "20px", height: "20px" }}
                />
            )
        }

        const channelName = (channel.meaning || channel.name || "").toLowerCase()

        if (channelName.includes("email")) return <i className="bx bx-envelope"></i>
        if (channelName.includes("phone")) return <i className="bx bx-phone"></i>
        if (channelName.includes("whatsapp"))
            return <i className="bx bxl-whatsapp"></i>

        return <i className="bx bx-info-circle"></i>
    }

    const channelOptions = commChannels.map(channel => ({
        value: channel.commChannelID,
        label: channel.name,
    }))

    return (
        <>
            {/* Communications Modal */}
            <Modal
                isOpen={showCommunicationsModal}
                toggle={() => setShowCommunicationsModal(false)}
                size="xl"
            >
                <ModalHeader toggle={() => setShowCommunicationsModal(false)}>
                    {selectedContact?.contactName} - Contact & Communications
                </ModalHeader>
                <ModalBody>
                    {/* Contact Section */}
                    <Card className="mb-4">
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0">Contact</h5>
                                {!editingContact && (
                                    <AccessControlWrapper
                                        contextUri={url.MANAGE_MERCHANT_GROUP_CONTACT}
                                        actionType="post"
                                    >
                                        <CButton.Primary
                                            text="Edit Contact"
                                            onClick={handleEditContact}
                                            disabled={showAddCommunication || editingCommId !== null}
                                        />
                                    </AccessControlWrapper>
                                )}
                            </div>

                            {editingContact ? (
                                <Form
                                    onSubmit={e => {
                                        e.preventDefault()
                                        contactEditValidation.handleSubmit()
                                        return false
                                    }}
                                >
                                    <Row>
                                        <Col md="4">
                                            <TextInput
                                                label="Contact Name"
                                                name="contactName"
                                                placeholder="Enter contact name"
                                                validation={contactEditValidation}
                                                maxLength={255}
                                                required={true}
                                            />
                                        </Col>
                                        <Col md="4">
                                            <TextInput
                                                label="Contact Designation"
                                                name="contactDesignation"
                                                placeholder="Enter contact designation"
                                                validation={contactEditValidation}
                                                maxLength={255}
                                                required={true}
                                            />
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label>Photo</Label>
                                                {contactSelectedFiles.length === 0 || contactEditValidation.errors.photo ? (
                                                    <>
                                                        <Dropzone
                                                            multiple={false}
                                                            accept={{
                                                                "image/*": [".png", ".jpeg", ".jpg"],
                                                            }}
                                                            onDrop={acceptedFiles => {
                                                                handleContactPhotoFiles(acceptedFiles)
                                                            }}
                                                        >
                                                            {({ getRootProps, getInputProps }) => (
                                                                <div className="dropzone">
                                                                    <div
                                                                        className="dz-message needsclick mt-2"
                                                                        {...getRootProps()}
                                                                        style={{ minHeight: "100px" }}
                                                                    >
                                                                        <input {...getInputProps()} />
                                                                        <div className="mb-2">
                                                                            <i className="display-6 text-muted bx bxs-cloud-upload" />
                                                                        </div>
                                                                        <h6>Drop photo here or click to upload</h6>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Dropzone>
                                                    </>
                                                ) : null}

                                                {contactSelectedFiles.length > 0 &&
                                                    !contactEditValidation.errors.photo ? (
                                                    <div className="dropzone-previews" id="file-previews">
                                                        {contactSelectedFiles.map((f, i) => {
                                                            return (
                                                                <Card
                                                                    className="mb-2 position-relative shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                                    key={i + "-file"}
                                                                >
                                                                    <div className="p-2">
                                                                        <IconButton
                                                                            icon="bx bxs-x-circle"
                                                                            variant="rounded-circle"
                                                                            className="position-absolute right-0 top-0 start-100 translate-middle"
                                                                            style={{
                                                                                backgroundColor: "transparent",
                                                                                border: "none",
                                                                                fontSize: "1.5rem",
                                                                                color: "#f46a6a",
                                                                            }}
                                                                            onClick={clearContactPhoto}
                                                                            title="Remove Contact Photo"
                                                                        />

                                                                        <Row className="align-items-center">
                                                                            <Col className="col-auto">
                                                                                <img
                                                                                    data-dz-thumbnail=""
                                                                                    height="80"
                                                                                    className="avatar-sm rounded bg-light"
                                                                                    alt={f.name || "Contact Photo"}
                                                                                    src={f?.preview}
                                                                                />
                                                                            </Col>
                                                                            {f.name && f.formattedSize ? (
                                                                                <Col>
                                                                                    <div className="text-muted font-weight-bold">
                                                                                        {f.name}
                                                                                    </div>
                                                                                    <p className="mb-0">
                                                                                        <strong>{f.formattedSize}</strong>
                                                                                    </p>
                                                                                </Col>
                                                                            ) : null}
                                                                        </Row>
                                                                    </div>
                                                                </Card>
                                                            )
                                                        })}
                                                    </div>
                                                ) : null}
                                                {contactEditValidation.errors.photo ? (
                                                    <small
                                                        className={`invalid-feedback d-inline-block align-top ${contactEditValidation.touched.photo && contactEditValidation.errors.photo
                                                                ? "w-50"
                                                                : "w-100"
                                                            }`}
                                                    >
                                                        {contactEditValidation.errors.photo}
                                                    </small>
                                                ) : null}

                                                {contactSelectedFiles.length === 0 || contactEditValidation.errors.photo ? (
                                                    <span className="text-muted text-end d-inline-block w-100">
                                                        jpg & png are allowed upto 1mb max (550x550)
                                                    </span>
                                                ) : null}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <div className="text-end">

                                        <CButton.Secondary
                                            text="Cancel"
                                            className="me-2"
                                            onClick={handleCancelContactEdit}
                                            disabled={isUpdatingContact}
                                        />

                                        <ConfirmButton
                                            validateBeforeConfirm={() =>
                                                validateBeforeConfirm(contactEditValidation)
                                            }
                                            onConfirm={() =>
                                                handleContactEditSubmit(contactEditValidation.values)
                                            }
                                            message="Are you sure you want to update this contact?"
                                            disabled={isUpdatingContact}
                                            renderButton={
                                                <CButton.Primary
                                                    text="Update Contact"
                                                    loading={isUpdatingContact}
                                                    disabled={isUpdatingContact}
                                                />
                                            }
                                        />
                                    </div>
                                </Form>
                            ) : (
                                <Row>
                                    <Col md="4">
                                        <div className="text-center">
                                            {selectedContact?.photo ? (
                                                <img
                                                    src={selectedContact.photo}
                                                    alt={selectedContact.contactName}
                                                    className="rounded-circle"
                                                    style={{
                                                        width: "80px",
                                                        height: "80px",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="bg-primary rounded-circle d-inline-flex align-items-center justify-content-center"
                                                    style={{ width: "80px", height: "80px" }}
                                                >
                                                    <span className="text-white fs-3">
                                                        {selectedContact?.contactName
                                                            ?.charAt(0)
                                                            ?.toUpperCase()}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md="8">
                                        <div className="mb-3">
                                            <Label className="fw-bold">Contact Name:</Label>
                                            <p className="mb-1">{selectedContact?.contactName}</p>
                                        </div>
                                        <div className="mb-3">
                                            <Label className="fw-bold">Designation:</Label>
                                            <p className="mb-1">
                                                {selectedContact?.contactDesignation}
                                            </p>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </CardBody>
                    </Card>

                    {/* Communications Section */}
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="card-title mb-0">Communications</h5>
                                {!showAddCommunication && !editingContact && (
                                    <CButton.Success
                                        icon="bx bx-plus"
                                        text="Add Communication"
                                        onClick={handleAddCommunication}
                                        disabled={editingCommId !== null}
                                    />
                                )}
                            </div>

                            {showAddCommunication && (
                                <Card className="mb-3">
                                    <CardBody>
                                        <h6 className="mb-3">
                                            {editingCommunication
                                                ? "Edit Communication"
                                                : "Add Communication"}
                                        </h6>
                                        <Form
                                            onSubmit={e => {
                                                e.preventDefault()
                                                communicationValidation.handleSubmit()
                                                return false
                                            }}
                                        >
                                            <Row>
                                                <Col md="6">
                                                    <SelectInput
                                                        name="commChannelID"
                                                        label="Communication Channel"
                                                        placeholder="Select Channel..."
                                                        options={channelOptions}
                                                        validation={communicationValidation}
                                                        required={true}
                                                        onSelectionChange={selectedOption => {
                                                            communicationValidation.setFieldValue(
                                                                "commChannelID",
                                                                selectedOption ? selectedOption.value : ""
                                                            )
                                                            communicationValidation.setFieldValue(
                                                                "channelValue",
                                                                ""
                                                            )
                                                        }}
                                                    />
                                                </Col>
                                                <Col md="6">
                                                    <TextInput
                                                        label="Channel Value"
                                                        name="channelValue"
                                                        placeholder="Enter channel value"
                                                        validation={communicationValidation}
                                                        required={true}
                                                    />
                                                </Col>
                                            </Row>
                                            <div className="text-end">
                                                <CButton.Secondary
                                                    text="Cancel"
                                                    className="me-2"
                                                    onClick={handleCancelCommunication}
                                                />
                                                <ConfirmButton
                                                    contextUri={url.MANAGE_MERCHANT_GROUP_CONTACT_COMMUNICATION}
                                                    actionType="post"
                                                    validateBeforeConfirm={() =>
                                                        validateBeforeConfirm(communicationValidation)
                                                    }
                                                    onConfirm={() =>
                                                        handleCommunicationModalSubmit(
                                                            communicationValidation.values
                                                        )
                                                    }
                                                    message={`Are you sure you want to ${editingCommunication ? "update" : "add"
                                                        } this communication?`}
                                                    renderButton={
                                                        <CButton.Primary
                                                            text={editingCommunication ? "Update" : "Add"}
                                                        />
                                                    }
                                                />
                                            </div>
                                        </Form>
                                    </CardBody>
                                </Card>
                            )}

                            <SimpleTableContainer
                                title=""
                                data={contactCommunications.map(comm => ({
                                    ...comm,
                                    id: comm.mgContactCommunicationID,
                                }))}
                                columns={[
                                    {
                                        key: "channel",
                                        label: "Channel",
                                        render: item => (
                                            <div className="d-flex align-items-center">
                                                <span className="me-2" style={{ filter: "invert(1)" }}>
                                                    {getChannelIcon(item.commChannel)}
                                                </span>
                                                {item.commChannel.name}
                                            </div>
                                        ),
                                    },
                                    {
                                        key: "channelValue",
                                        label: "Value",
                                    },
                                ]}
                                loading={isLoadingContactCommunications}
                                emptyMessage="No communications added yet"
                                emptyIcon="bx bx-info-circle"
                                enableInlineEdit={true}
                                editingId={editingCommId}
                                editValue={editCommValue}
                                onEditValueChange={setEditCommValue}
                                editableField="channelValue"
                                onEdit={handleEditCommunication}
                                onSave={handleSaveCommunicationEdit}
                                onCancel={handleCancelCommunicationEdit}
                                onDelete={handleDeleteCommunication}
                                customActions={item => {
                                    if (editingCommId === item.mgContactCommunicationID) {
                                        return (
                                            <>
                                                <ConfirmButton
                                                    onConfirm={() => handleSaveCommunicationEdit(item)}
                                                    message="Are you sure you want to update this communication?"
                                                    renderButton={<IconButton.Save className="me-2" />}
                                                />
                                                <IconButton.Cancel
                                                    onClick={handleCancelCommunicationEdit}
                                                />
                                            </>
                                        )
                                    }

                                    return (
                                        <>
                                            <AccessControlWrapper
                                                contextUri={url.MANAGE_MERCHANT_GROUP_CONTACT_COMMUNICATION}
                                                actionType="post"
                                            >
                                                <IconButton.Edit
                                                    className="me-2"
                                                    onClick={() => handleEditCommunication(item)}
                                                    disabled={
                                                        showAddCommunication || editingCommId !== null
                                                    }
                                                />
                                            </AccessControlWrapper>
                                            <ConfirmButton
                                                contextUri={url.MANAGE_MERCHANT_GROUP_CONTACT_COMMUNICATION}
                                                actionType="post"
                                                onConfirm={() => handleDeleteCommunication(item)}
                                                message="Are you sure you want to delete this communication?"
                                                disabled={
                                                    showAddCommunication || editingCommId !== null
                                                }
                                                renderButton={<IconButton.Delete />}
                                            />
                                        </>
                                    )
                                }}
                                className="border-0 shadow-none"
                            />
                        </CardBody>
                    </Card>
                </ModalBody>
            </Modal>

            {/* Main Content */}

            {contactError && shouldDisplayError(contactError) && (
                <Alert color="danger" className="mb-3">
                    {contactError.message || "An error occurred"}
                </Alert>
            )}

            {/* Two-Phase Contact Creation Form */}
            {showAddForm && (
                <Card className="mb-4">
                    <CardBody>
                        <h6 className="mb-3">
                            {contactCreationState.phase === "contact"
                                ? "Add New Contact"
                                : `Add Communications for ${contactCreationState.currentContact?.contactName}`}
                        </h6>

                        {/* Phase 1: Contact Information */}
                        {contactCreationState.phase === "contact" && (
                            <Form
                                onSubmit={e => {
                                    e.preventDefault()
                                    validation.handleSubmit()
                                    return false
                                }}
                            >
                                <Row>
                                    <Col md="6">
                                        <TextInput
                                            label="Contact Name"
                                            name="contactName"
                                            placeholder="Enter contact name"
                                            validation={validation}
                                            maxLength={255}
                                            required={true}
                                        />
                                    </Col>
                                    <Col md="6">
                                        <TextInput
                                            label="Contact Designation"
                                            name="contactDesignation"
                                            placeholder="Enter contact designation"
                                            validation={validation}
                                            maxLength={255}
                                            required={true}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>Photo</Label>

                                            {selectedFiles.length === 0 || validation.errors.photo ? (
                                                <>
                                                    <Dropzone
                                                        multiple={false}
                                                        accept={{
                                                            "image/*": [".png", ".jpeg", ".jpg"],
                                                        }}
                                                        onDrop={acceptedFiles => {
                                                            handleAcceptedFiles(acceptedFiles)
                                                        }}
                                                    >
                                                        {({ getRootProps, getInputProps }) => (
                                                            <div className="dropzone">
                                                                <div
                                                                    className="dz-message needsclick mt-2"
                                                                    {...getRootProps()}
                                                                >
                                                                    <input {...getInputProps()} />
                                                                    <div className="mb-3">
                                                                        <i className="display-4 text-muted bx bxs-cloud-upload" />
                                                                    </div>
                                                                    <h4>
                                                                        Drop contact photo here or click to upload.
                                                                    </h4>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Dropzone>
                                                </>
                                            ) : null}

                                            {selectedFiles.length > 0 && !validation.errors.photo ? (
                                                <div
                                                    className="dropzone-previews mt-3"
                                                    id="file-previews"
                                                >
                                                    {selectedFiles.map((f, i) => {
                                                        return (
                                                            <Card
                                                                className="mt-1 mb-2 position-relative shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                                                key={i + "-file"}
                                                            >
                                                                <div className="p-2">
                                                                    <IconButton
                                                                        icon="bx bxs-x-circle"
                                                                        variant="rounded-circle"
                                                                        className="position-absolute right-0 top-0 start-100 translate-middle"
                                                                        style={{
                                                                            backgroundColor: "transparent",
                                                                            border: "none",
                                                                            fontSize: "1.5rem",
                                                                            color: "#f46a6a",
                                                                        }}
                                                                        onClick={clearPhoto}
                                                                        title={"Remove Photo"}
                                                                    />

                                                                    <Row className="align-items-center">
                                                                        <Col className="col-auto">
                                                                            <img
                                                                                data-dz-thumbnail=""
                                                                                height="80"
                                                                                className="avatar-sm rounded bg-light"
                                                                                alt={f.name || "Contact Photo"}
                                                                                src={f?.preview}
                                                                            />
                                                                        </Col>
                                                                        {f.name && f.formattedSize ? (
                                                                            <Col>
                                                                                <div className="text-muted font-weight-bold">
                                                                                    {f.name}
                                                                                </div>
                                                                                <p className="mb-0">
                                                                                    <strong>{f.formattedSize}</strong>
                                                                                </p>
                                                                            </Col>
                                                                        ) : null}
                                                                    </Row>
                                                                </div>
                                                            </Card>
                                                        )
                                                    })}
                                                </div>
                                            ) : null}

                                            {validation.errors.photo ? (
                                                <small
                                                    className={`invalid-feedback d-inline-block align-top ${validation.touched.photo && validation.errors.photo
                                                        ? "w-50"
                                                        : "w-100"
                                                        }`}
                                                >
                                                    {validation.errors.photo}
                                                </small>
                                            ) : null}
                                            {selectedFiles.length === 0 || validation.errors.photo ? (
                                                <span
                                                    className={`"text-muted text-end ${readOnly ? "d-none" : "d-inline-block"
                                                        } ${validation.touched.photo && validation.errors.photo
                                                            ? "w-50"
                                                            : "w-100"
                                                        }`}
                                                >
                                                    jpg & png are allowed upto 1mb max (550x550)
                                                </span>
                                            ) : null}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <div className="text-end">
                                    {
                                        page !== "add" ? (
                                            <CButton.Secondary
                                                text="Cancel"
                                                className="me-2"
                                                onClick={handleCancelContactCreation}
                                                disabled={isSubmitting}
                                            />
                                        ) : null
                                    }


                                    <ConfirmButton
                                        contextUri={url.MANAGE_MERCHANT_GROUP_CONTACT}
                                        actionType="post"
                                        validateBeforeConfirm={() =>
                                            validateBeforeConfirm(validation)
                                        }
                                        onConfirm={() => handleContactSubmit(validation.values)}
                                        message="Are you sure you want to add this contact?"
                                        disabled={isSubmitting}
                                        renderButton={
                                            <CButton.Primary
                                                text="Add Contact"
                                                loading={isSubmitting}
                                                disabled={isSubmitting}
                                            />
                                        }
                                    />
                                </div>
                            </Form>
                        )}

                        {/* Phase 2: Communication Management */}
                        {contactCreationState.phase === "communication" && (
                            <>
                                {/* Contact Summary (Read-only) */}
                                <Card className="mb-4 border-success">
                                    <CardBody className="bg-light">
                                        <Row>
                                            <Col md="2">
                                                <div className="text-center">
                                                    {contactCreationState.currentContact?.photo ? (
                                                        <img
                                                            src={contactCreationState.currentContact.photo}
                                                            alt={
                                                                contactCreationState.currentContact.contactName
                                                            }
                                                            className="rounded-circle"
                                                            style={{
                                                                width: "60px",
                                                                height: "60px",
                                                                objectFit: "cover",
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="bg-success rounded-circle d-inline-flex align-items-center justify-content-center"
                                                            style={{ width: "60px", height: "60px" }}
                                                        >
                                                            <span className="text-white fs-4">
                                                                {contactCreationState.currentContact?.contactName
                                                                    ?.charAt(0)
                                                                    ?.toUpperCase()}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                            <Col md="10">
                                                <div className="d-flex align-items-center h-100">
                                                    <div>
                                                        <h5 className="mb-1 text-success">
                                                            <i className="bx bx-check-circle me-2"></i>
                                                            Contact Created Successfully
                                                        </h5>
                                                        <p className="mb-1">
                                                            <strong>Name:</strong>{" "}
                                                            {contactCreationState.currentContact?.contactName}
                                                        </p>
                                                        <p className="mb-0">
                                                            <strong>Designation:</strong>{" "}
                                                            {
                                                                contactCreationState.currentContact
                                                                    ?.contactDesignation
                                                            }
                                                        </p>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>

                                {/* Communication Form */}
                                <Form
                                    onSubmit={e => {
                                        e.preventDefault()
                                        communicationPhaseValidation.handleSubmit()
                                        return false
                                    }}
                                >
                                    {/* Error Display for Communications */}
                                    {contactCommunicationError &&
                                        shouldDisplayError(contactCommunicationError) && (
                                            <Alert color="warning" className="mb-3">
                                                <i className="bx bx-error-circle me-2"></i>
                                                Communication Error:{" "}
                                                {contactCommunicationError.message ||
                                                    "Failed to add some communications. You can retry or skip this step."}
                                            </Alert>
                                        )}

                                    <div>
                                        <Label className="fw-bold">Communications (Optional)</Label>
                                    </div>

                                    {/* Show added communications table above input fields */}
                                    {contactCommunications &&
                                        contactCommunications.length > 0 && (
                                            <SimpleTableContainer
                                                title=""
                                                data={contactCommunications.map(comm => ({
                                                    ...comm,
                                                    id: comm.mgContactCommunicationID,
                                                }))}
                                                columns={[
                                                    {
                                                        key: "channel",
                                                        label: "Channel",
                                                        render: item => (
                                                            <div className="d-flex align-items-center">
                                                                <span
                                                                    className="me-2"
                                                                    style={{ filter: "invert(1)" }}
                                                                >
                                                                    {getChannelIcon(item.commChannel)}
                                                                </span>
                                                                {item.commChannel.name}
                                                            </div>
                                                        ),
                                                    },
                                                    {
                                                        key: "channelValue",
                                                        label: "Value",
                                                    },
                                                ]}
                                                loading={isLoadingContactCommunications}
                                                emptyMessage="No communications added yet"
                                                emptyIcon="bx bx-info-circle"
                                                showActions={false}
                                                className="border-0 shadow-none mb-0"
                                            />
                                        )}

                                    {/* Single communication input form */}
                                    <Card className="mb-3 border">
                                        <CardBody className="py-3">
                                            <Row className="align-items-end">
                                                <Col md="5">
                                                    <SelectInput
                                                        name="commChannelID"
                                                        label={`Communication Channel`}
                                                        placeholder="Select Channel..."
                                                        options={channelOptions}
                                                        validation={communicationPhaseValidation}
                                                        required={false}
                                                        onSelectionChange={selectedOption => {
                                                            updateCommunicationField(
                                                                "commChannelID",
                                                                selectedOption
                                                                    ? selectedOption.value.toString()
                                                                    : ""
                                                            )
                                                        }}
                                                    />
                                                </Col>
                                                <Col md="5">
                                                    <FormGroup>
                                                        <Label>
                                                            Channel Value
                                                            {communicationPhaseValidation.values
                                                                .commChannelID &&
                                                                typeof communicationPhaseValidation.values
                                                                    .commChannelID === "string" &&
                                                                communicationPhaseValidation.values.commChannelID.trim() !==
                                                                ""
                                                                ? "*"
                                                                : ""}
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            value={
                                                                communicationPhaseValidation.values.channelValue
                                                            }
                                                            onChange={e =>
                                                                updateCommunicationField(
                                                                    "channelValue",
                                                                    e.target.value
                                                                )
                                                            }
                                                            onBlur={() => {
                                                                communicationPhaseValidation.setFieldTouched(
                                                                    "channelValue",
                                                                    true
                                                                )
                                                                communicationPhaseValidation.setFieldTouched(
                                                                    "commChannelID",
                                                                    true
                                                                )
                                                            }}
                                                            placeholder="Enter channel value"
                                                            invalid={
                                                                communicationPhaseValidation.touched
                                                                    .channelValue &&
                                                                communicationPhaseValidation.errors.channelValue
                                                            }
                                                        />
                                                        {communicationPhaseValidation.touched
                                                            .channelValue &&
                                                            communicationPhaseValidation.errors
                                                                .channelValue && (
                                                                <FormFeedback>
                                                                    {
                                                                        communicationPhaseValidation.errors
                                                                            .channelValue
                                                                    }
                                                                </FormFeedback>
                                                            )}
                                                    </FormGroup>
                                                </Col>
                                                {communicationPhaseValidation.values.commChannelID &&
                                                    typeof communicationPhaseValidation.values
                                                        .commChannelID === "string" &&
                                                    communicationPhaseValidation.values.commChannelID.trim() !==
                                                    "" &&
                                                    communicationPhaseValidation.values.channelValue &&
                                                    communicationPhaseValidation.values.channelValue.trim() !==
                                                    "" && (
                                                        <Col md="2" className="d-flex align-items-end">
                                                            <FormGroup>
                                                                <IconButton.Save
                                                                    onClick={() => {
                                                                        // Use the proper Yup validation instead of bypassing it
                                                                        communicationPhaseValidation.handleSubmit()
                                                                    }}
                                                                    title="Save this communication"
                                                                    disabled={isSubmittingCommunication}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    )}
                                            </Row>
                                        </CardBody>
                                    </Card>

                                    <div className="text-end">
                                        {/* Check if any communications have been added */}
                                        {contactCommunications &&
                                            contactCommunications.length > 0 ? (
                                            <CButton.Primary
                                                text="Finish"
                                                onClick={handleFinishCommunications}
                                                disabled={isSubmittingCommunication}
                                            />
                                        ) : (
                                            <CButton.Secondary
                                                text="Skip"
                                                onClick={handleSkipCommunications}
                                                disabled={isSubmittingCommunication}
                                            />
                                        )}
                                    </div>
                                </Form>
                            </>
                        )}
                    </CardBody>
                </Card>
            )}

            {/* Contacts List */}
            <SimpleTableContainer
                title="Contacts"
                data={contacts.map(contact => ({
                    ...contact,
                    id: contact.merchantGroupContactID,
                }))}
                columns={[
                    {
                        key: "photo",
                        label: "Photo",
                        width: "80px",
                        align: "center",
                        render: item => {
                            if (item.photo) {
                                return (
                                    <img
                                        src={item.photo}
                                        alt={item.contactName}
                                        className="avatar-xs rounded-circle"
                                    />
                                )
                            }
                            return (
                                <div className="avatar-xs mx-auto">
                                    <span className="avatar-title avatar-xs rounded-circle bg-primary text-white">
                                        {item.contactName.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                            )
                        },
                    },
                    {
                        key: "contactName",
                        label: "Name",
                    },
                    {
                        key: "contactDesignation",
                        label: "Designation",
                    },
                ]}
                loading={isLoadingContacts}
                emptyMessage="No contacts added yet"
                emptyIcon="bx bx-user-circle"
                customActions={item => (
                    <>
                        <IconButton.View
                            className="me-2"
                            onClick={() => handleViewCommunications(item)}
                        />
                        <ConfirmButton
                            contextUri={url.MANAGE_MERCHANT_GROUP_CONTACT}
                            actionType="post"
                            onConfirm={() => handleDeleteContact(item)}
                            message="Are you sure you want to delete this contact?"
                            disabled={isSubmitting}
                            renderButton={<IconButton.Delete />}
                        />
                    </>
                )}
                headerActions={
                    page !== "add" &&
                    !showAddForm && (
                        <AccessControlWrapper
                            contextUri={url.MANAGE_MERCHANT_GROUP_CONTACT}
                            actionType="post"
                        >
                            <CButton.Success
                                icon="bx bx-plus"
                                text="Add Contact"
                                onClick={handleAddNewContact}
                            />
                        </AccessControlWrapper>
                    )
                }
            />
        </>
    )
}
